import { Alert, Offcanvas } from "react-bootstrap";
import IconInputWidget from "../JsonForm/theme/widgets/IconInputWidget";
import { useContext, useState } from "react";
import { AppContext } from "components/App/AppContext";
import {
    updateGuestAccount,
    useAccountPendingVerification,
    usePasswordRequirements,
    userNewAuthCode,
    verifyGuestAccountUpdate,
} from "../utils";
import { getCustomerNumber, getPendingAccountCustomerNumber } from "components/utils/user";
import { ErrorSummary } from "../ErrorSummary";
import { JsonForm } from "../JsonForm";
import { SubmitButton } from "../Button/SubmitButton";
import { Heading } from "../Heading";
import { Button } from "../Button";

type AccountFormProps = {
    show: boolean;
    onClose: () => void;
    onCompleted?: () => void;
};

export const AccountForm = ({ show, onClose, onCompleted }: AccountFormProps) => {
    const validated = useAccountPendingVerification();

    return (
        <Offcanvas placement="end" show={show} onHide={onClose}>
            <Offcanvas.Header closeButton className="px-4">
                <Offcanvas.Title>Create Account</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="px-4">
                {validated ? (
                    <AuthCodeForm
                        onVerified={() => {
                            onClose();
                            onCompleted?.();
                        }}
                    />
                ) : (
                    <AccountDetailsForm onCancel={onClose} />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

const widgets = { iconInput: IconInputWidget };

const transformErrors = (errors: any[]) =>
    errors.map((e) => (e.name === "required" ? { ...e, message: "Please fill out this field." } : e));

const accountSchema = {
    type: "object",
    required: ["firstName", "lastName", "password", "repeatPassword"],
    properties: {
        firstName: {
            type: "string",
            title: "First Name",
        },
        lastName: {
            type: "string",
            title: "Last Name",
        },
        password: {
            type: "string",
            title: "Password",
        },
        repeatPassword: {
            type: "string",
            title: "Re-Enter Password",
        },
    },
};

const AccountDetailsForm = ({ onCancel }: { onCancel: () => void }) => {
    const { programNumber } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const [passwordRequirements, isLoading] = usePasswordRequirements();
    const passwordHelpText = !isLoading && passwordRequirements ? passwordRequirements : undefined;

    const accountUiSchema = {
        firstName: {
            "ui:placeholder": "Enter First Name",
            "ui:options": { autoComplete: "given-name" },
        },
        lastName: {
            "ui:placeholder": "Enter Last Name",
            "ui:options": { autoComplete: "family-name" },
        },
        password: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", helpIcon: true, autoComplete: "new-password" },
            "ui:help": passwordHelpText,
            "ui:placeholder": "Enter your password",
        },
        repeatPassword: {
            "ui:widget": "iconInput",
            "ui:options": { icon: "lock", inputType: "password", autoComplete: "new-password" },
            "ui:placeholder": "Re-Enter your password",
        },
    };

    function validation(formData: any, errors: any) {
        if (formData.repeatPassword && formData.password !== formData.repeatPassword) {
            errors.repeatPassword.addError("Passwords don't match");
        }
        return errors;
    }

    const onSubmit = async ({ formData }: any) => {
        setErrorSummary(undefined);
        setIsSubmitting(true);

        updateGuestAccount({
            firstName: formData.firstName,
            lastName: formData.lastName,
            password: formData.password,
            programNumber,
        })
            .catch((error: any) => {
                setErrorSummary(error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <Heading level="h6" className="mb-4">
                Add the following details:
            </Heading>
            {errorSummary && <ErrorSummary errorSummary={errorSummary} />}
            <JsonForm
                schema={accountSchema}
                uiSchema={accountUiSchema}
                widgets={widgets}
                onSubmit={onSubmit}
                validate={validation}
                isSubmitting={isSubmitting}
                formName="Create Account"
                buttonClassName="mt-3"
                transformErrors={transformErrors}
            >
                <SubmitButton type="submit" variant="primary" isSubmitting={isSubmitting} spinnerText="Loading...">
                    Save
                </SubmitButton>
                <Button variant="secondary" className="ms-2" disabled={isSubmitting} onClick={onCancel}>
                    Cancel
                </Button>
            </JsonForm>
        </>
    );
};

const authenticationSchema = {
    type: "object",
    required: ["authCode"],
    properties: {
        authCode: {
            type: "string",
            title: "Authorization Code",
        },
    },
};

const authenticationUiSchema = {
    authCode: {
        "ui:placeholder": "Enter the Authorization Code",
    },
};

const AuthCodeForm = ({ onVerified }: { onVerified: () => void }) => {
    const { programNumber } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorSummary, setErrorSummary] = useState<any>();
    const [showGotNewAuthCodeMessage, setShowGotNewAuthCodeMessage] = useState(false);

    const onSubmit = async ({ formData }: any) => {
        setErrorSummary(undefined);
        setIsSubmitting(true);

        verifyGuestAccountUpdate({
            userNumber: getPendingAccountCustomerNumber() ?? getCustomerNumber(),
            authCode: formData.authCode,
        })
            .then(() => {
                onVerified();
            })
            .catch((error: any) => {
                setErrorSummary(error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const onNewAuthCode = () => {
        setErrorSummary(undefined);
        setShowGotNewAuthCodeMessage(false);

        userNewAuthCode({ programNumber })
            .then(() => {
                setShowGotNewAuthCodeMessage(true);
            })
            .catch((error: any) => {
                setErrorSummary(error);
            });
    };

    return (
        <>
            <Heading level="h6" className="mb-2">
                Check your email
            </Heading>
            <p className="text-secondary">We've sent an authorization code to your email. Enter it below to finish creating your account.</p>
            {errorSummary && <ErrorSummary errorSummary={errorSummary} />}
            <Alert dismissible onClose={() => setShowGotNewAuthCodeMessage(false)} show={showGotNewAuthCodeMessage} variant="success">
                <p className="mb-0">A new authorization code has been sent to your email.</p>
            </Alert>
            <JsonForm
                schema={authenticationSchema}
                uiSchema={authenticationUiSchema}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
                formName="Authorization Code"
                buttonClassName="mt-3"
            >
                <SubmitButton type="submit" variant="primary" isSubmitting={isSubmitting} spinnerText="Loading...">
                    Submit
                </SubmitButton>
                <Button variant="link" className="ms-2" disabled={isSubmitting} onClick={onNewAuthCode}>
                    Get New Authorization Code
                </Button>
            </JsonForm>
        </>
    );
};
